import { useTranslation } from 'react-i18n-lite'

import {
  AvatarList,
  Divider,
  SafeHtmlContent,
  SwipeableSlider,
  Text,
  YesObject
} from '@yes.technology/react-toolkit'

import { updateStatus } from 'status/shared'
import { StatusAction } from 'status/shared/situation.type'

import { useFetchYesObjects } from 'yesObject/shared'

import TaskActions from 'component/TaskObjectCard/TaskActions'
import {
  Container,
  Content,
  PriorityBadge
} from 'component/TaskObjectCard/TaskObjectCard.styles'
import { TaskPriority } from 'component/TaskObjectCard/TaskObjectCard.types'
import useAssociatedUsers from 'component/TaskObjectCard/hooks/useAssociatedUsers/useAssociatedUsers'

import { useMemo } from 'react'
import InformationalFadingContainer from 'site/Renderer/components/InformationalFadingContainer/InformationalFadingContainer'
import useKanbanContext from 'site/Renderer/components/Kanban/components/KanbanAutoColumns/useKanbanContext'

import { AMFTaskObjectCardProps } from './AMFTaskObjectCard.types'
import {
  AMFTaskCard,
  AMF_TASK_CARD_MUTED_COLOR,
  SliderItem
} from './AMFTaskObjectCard.styles'
import TaskCardFieldSlide from './components/TaskCardFieldSlide'
import filterAssociatedUsersRelations from './helpers/filterAssociatedUsersRelations'
import getSwipeableSliderLayout from './helpers/getSwipeableSliderLayout'

function taskToAvatarListAdapter(objects: YesObject[]) {
  return objects.map((object) => ({
    ...object,
    title: object.des
  }))
}

const defaultPriority: TaskPriority = 'normal'

export default function AMFTaskObjectCard({
  priority = defaultPriority,
  des,
  complement,
  uuid_status: statusUuid,
  version,
  uuid,
  uuid_Objectclass,
  uuid_Taskmodel,
  workflow,
  returnOnFinish,
  buttonProps,
  fields = {},
  cardConfiguration,
  ...otherProps
}: AMFTaskObjectCardProps) {
  const { t, language } = useTranslation()

  const { yesObjects: statusActions } = useFetchYesObjects<StatusAction>({
    filterBody: {
      query: { $and: [{ 'allowed_origin_status.code': { $eq: statusUuid } }] },
      objectclass: globalThis.uuidObjectClassStatusAction
    }
  })

  const {
    onStatusChange,
    statusActionClassification,
    associatedUsersRelations,
    visualizeUrl
  } = useKanbanContext()

  const filteredAssociatedUsersRelations = useMemo(
    () =>
      filterAssociatedUsersRelations(
        associatedUsersRelations,
        cardConfiguration.associated_users_list
      ),
    [associatedUsersRelations, cardConfiguration.associated_users_list]
  )

  const { data: associatedUsers, isLoading: isLoadingAssociatedUsers } =
    useAssociatedUsers({
      taskUuid: uuid,
      associatedUsersRelations: filteredAssociatedUsersRelations,
      data: { ...otherProps }
    })

  const classification = useMemo(
    () =>
      statusActionClassification?.find(
        (classification) => classification.UUID_OBJECT === statusUuid
      ),
    [statusActionClassification, statusUuid]
  )

  const changeStatus = (destinationStatusCode: string) => {
    updateStatus({
      objectVersion: version,
      objectUuid: uuid,
      objectclassUuid: uuid_Objectclass,
      destinationStatusCode,
      showLoading: false
    })

    onStatusChange?.(uuid, destinationStatusCode)
  }

  const associatedUsersList = Object.entries(associatedUsers)

  const priorityValue =
    typeof priority === 'string'
      ? priority
      : ((priority?.[0]?.complement || defaultPriority) as TaskPriority)

  const processLayout = getSwipeableSliderLayout(
    cardConfiguration.process_information_area.length
  )
  const usersLayout = getSwipeableSliderLayout(
    cardConfiguration.associated_users_list.length
  )
  const timestampsLayout = getSwipeableSliderLayout(
    cardConfiguration.timestamps_area.length
  )

  const taskData = { des, complement, ...otherProps }

  return (
    <Container>
      <AMFTaskCard>
        <Content>
          <PriorityBadge priority={priorityValue}>
            {t(`task.priority.${priorityValue}`)}
          </PriorityBadge>

          <SwipeableSlider itemsPerPage={processLayout.itemsPerPage}>
            {cardConfiguration.process_information_area.map(
              (fieldKey, index) => (
                <SliderItem
                  key={fieldKey}
                  $hasGap={
                    index <
                    cardConfiguration.process_information_area.length - 1
                  }
                >
                  <TaskCardFieldSlide
                    fieldKey={fieldKey}
                    field={fields[fieldKey]}
                    value={taskData[fieldKey as keyof typeof taskData]}
                    language={language}
                  />
                </SliderItem>
              )
            )}
          </SwipeableSlider>

          <h2>{des}</h2>
          {complement && (
            <InformationalFadingContainer>
              <Text variant='content-03'>
                <SafeHtmlContent html={complement} />
              </Text>
            </InformationalFadingContainer>
          )}

          <Divider heightClass='small' color='lightGray' />

          {!isLoadingAssociatedUsers && (
            <SwipeableSlider itemsPerPage={usersLayout.itemsPerPage}>
              {associatedUsersList.map(([role, objects], index) => (
                <SliderItem
                  key={role}
                  $hasGap={index < associatedUsersList.length - 1}
                >
                  <AvatarList
                    objects={taskToAvatarListAdapter(objects)}
                    title={role}
                    backgroundColor={AMF_TASK_CARD_MUTED_COLOR}
                    bottomListLimit={4}
                  />
                </SliderItem>
              ))}
            </SwipeableSlider>
          )}

          <Divider heightClass='small' color='lightGray' />

          <SwipeableSlider itemsPerPage={timestampsLayout.itemsPerPage}>
            {cardConfiguration.timestamps_area.map((fieldKey, index) => (
              <SliderItem
                key={fieldKey}
                $hasGap={index < cardConfiguration.timestamps_area.length - 1}
              >
                <TaskCardFieldSlide
                  fieldKey={fieldKey}
                  field={fields[fieldKey]}
                  value={taskData[fieldKey as keyof typeof taskData]}
                  language={language}
                />
              </SliderItem>
            ))}
          </SwipeableSlider>

          <TaskActions
            {...{
              objectclassUuid: uuid_Objectclass,
              statusActions,
              changeStatus,
              statusUuid,
              returnOnFinish,
              taskUuid: uuid,
              taskmodelUuid: uuid_Taskmodel,
              workflow,
              des,
              buttonProps,
              visualizeUrl,
              classification,
              variant: 'synthetic'
            }}
          />
        </Content>
      </AMFTaskCard>
    </Container>
  )
}
